/**
 * App-facing one-stop helper that bundles every builtin alias factory
 * a typical client needs. Pass the bundled array as
 * `SheetModelCacheDeps.builtinAliases` and you get the standard set
 * of pure-static templates (`weather`, `timezone`, `data`, `task`,
 * `calendar`, `calendar-allday`) plus the client-hydrated ones
 * (`calendar-list` and `task-list` always, fed by no-op
 * `CalendarConfigProvider` / `TaskListConfigProvider` when none is
 * supplied; `alias.secondary-timezones` and
 * `alias.default-weather` if `CalendarPreferences` is supplied).
 *
 * `calendar-list` / `task-list` are registered unconditionally because
 * the `calendar` / `task` alias subtrees reference them by name;
 * refusing to register them leaves those templates unable to expand.
 * When the caller supplies no provider, the factory uses one that
 * returns an empty list — the template expands with no per-list
 * children (calendar-list: no calendars; task-list: only the static
 * `:~lona:tasks` default), which is correct for tests + sheets where
 * the user hasn't connected an account.
 *
 * Apps that want only a subset can compose factories directly — this
 * helper is just sugar.
 */

import type {
  CalendarConfigProvider,
  TaskListConfigProvider,
} from "@tento-lona/sheets";
import { BuiltinAliasFactory, type Option } from "@tento-lona/sheets";

import type { CalendarPreferences } from "./calendar-preferences.ts";
import { calendarListAliasFactory } from "./calendar-aliases.ts";
import {
  defaultWeatherAliasFactory,
  secondaryTimezonesAliasFactory,
} from "./preferences-aliases.ts";
import { taskListAliasFactory } from "@tento-lona/sheets/internal-sdk";

/**
 * The providers the built-in alias factories read: calendars, task lists and
 * calendar preferences.
 */
export interface BuiltinAliasFactoriesOptions {
  readonly calendarConfig?: CalendarConfigProvider;
  readonly taskListConfig?: TaskListConfigProvider;
  readonly calendarPrefs?: CalendarPreferences;
  /**
   * The viewer's weather location id, read when `alias.default-weather`
   * expands with no preference set (`viewerWeatherLocationOf`); absent, such
   * an alias has no location.
   */
  readonly viewerWeatherLocation?: () => Option<string>;
}

/**
 * Empty provider — `calendars()` returns no items so the
 * calendar-list template's `dynamicTail` produces zero per-
 * calendar children. Used by the unconditional registration
 * below when the caller didn't supply a real provider.
 */
const EMPTY_CALENDAR_CONFIG: CalendarConfigProvider = {
  calendars: () => [],
};

/**
 * Empty provider — `taskLists()` returns no items so the task-list
 * template's `dynamicTail` produces zero per-list children (only the
 * static `:~lona:tasks` default survives). Used when the caller didn't
 * supply a real provider (tests, sheets where lists haven't loaded).
 */
const EMPTY_TASK_LIST_CONFIG: TaskListConfigProvider = {
  taskLists: () => [],
};

/**
 * The built-in alias factories (calendar list, task lists, preference
 * aliases) a client expands aliases with.
 */
export function builtinAliasFactories(
  opts: BuiltinAliasFactoriesOptions = {},
): readonly BuiltinAliasFactory[] {
  const factories: BuiltinAliasFactory[] = [
    ...BuiltinAliasFactory.defaults(),
    calendarListAliasFactory(opts.calendarConfig ?? EMPTY_CALENDAR_CONFIG),
    taskListAliasFactory(opts.taskListConfig ?? EMPTY_TASK_LIST_CONFIG),
  ];
  if (opts.calendarPrefs) {
    factories.push(secondaryTimezonesAliasFactory(opts.calendarPrefs));
    factories.push(
      defaultWeatherAliasFactory(opts.calendarPrefs, opts.viewerWeatherLocation),
    );
  }
  return factories;
}
